import { Fragment, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DataTableSkeleton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from "@carbon/react";
import "./dataTable.scss";

/**
 * The only thing this primitive requires of a row beyond the values it
 * displays is a stable identity to key on. Every resource this table will
 * ever show (users, sites, tags, materials, ...) already has one, so this
 * is a floor, not a real restriction.
 */
export interface DataTableRow {
  id: string | number;
}

/**
 * `key` is tied to `T` (`Extract<keyof T, string>`, not a bare `string`) so
 * a typo'd column key is a compile error, not a silent `undefined` cell at
 * runtime. This costs nothing at real call sites: `T` here is always a
 * consumer-authored "view row" shaped for display (formatted labels,
 * already-rendered action nodes, ...), never the raw API entity, so an
 * action column just needs a real `actions: ReactNode` property on that
 * view-row type rather than a synthetic one bolted on to satisfy the type
 * checker. Checked against both a Users-shaped row (derived `role`/
 * `responsible` columns, a trailing `actions` link) and a Sites-shaped row
 * (an `internalCode`/`name`/`address`/`comment` row plus the same trailing
 * `actions` link) — both type-check cleanly with zero extra properties.
 *
 * `header` is `ReactNode`, not `string`: Tags' bulk-assign table needs a
 * select-all checkbox *in* a column header, which a `string` header can
 * never express. Widening (rather than adding a separate `renderHeader`)
 * keeps this to one field with one always-correct source of truth, and is
 * backward compatible — every existing plain-string header still satisfies
 * `ReactNode`. Row-level selection (highlighted/click-to-toggle rows) is
 * NOT supported yet; it stays out until a real consumer needs it rather
 * than being guessed at here.
 */
export interface DataTableColumn<T extends DataTableRow> {
  key: Extract<keyof T, string>;
  header: ReactNode;
  /**
   * Opt-in: this column's header is clickable to sort (only meaningful with `DataTableProps['sort']`).
   * Omitted/false renders a plain header. Server-side sorting is the caller's job — `sort.onSort`
   * fires with this column's key and the caller refetches.
   */
  sortable?: boolean;
}

/** The shared items-per-page choices every server-paginated list offers. One source so the lists
 * (Materials, Orders, Suppliers, Users, Deliveries, …) can't drift apart. */
export const PAGE_SIZES = [10, 20, 50];

export interface DataTableSearchProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

/**
 * Opt-in server-side sorting. `columnKey` is the currently-sorted column's key (null = none);
 * `direction` is its direction; `onSort(columnKey)` fires when a sortable header is clicked (the
 * caller decides the toggle/refetch). Only columns with `sortable: true` render a sort control.
 */
export interface DataTableSortProps {
  columnKey: string | null;
  direction: "asc" | "desc";
  onSort: (columnKey: string) => void;
}

export interface DataTablePaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number, pageSize: number) => void;
  /**
   * The selectable page sizes for Carbon's items-per-page dropdown. Omitted (the Users/Sites/Tags
   * convention) leaves the dropdown showing only the fixed `pageSize` — a non-interactive display
   * value. Provided (e.g. `[10, 20, 50]`), the dropdown becomes a real control and a change fires
   * `onPageChange(page, pageSize)` with the chosen size, so the caller can refetch that many rows.
   */
  pageSizes?: number[];
}

/**
 * Row-selection capability, opt-in via `DataTableProps['selection']` — deliberately deferred
 * (see this file's own history) "until Tags is the real consumer", which is now: Task 5's
 * bulk-assign materials table. Omitting this prop entirely (what Users/Sites — the two flows
 * that already consume `DataTable` — do today) renders no checkbox column at all, so neither
 * screen changes by one byte.
 *
 * `selectedIds`/`onChange` follow the same controlled-from-the-caller convention `search` and
 * `pagination` already use above: `DataTable` never holds selection state itself, it only ever
 * renders exactly what `selectedIds` says and reports intent through `onChange` — the caller
 * (a `useState` today, whatever Task 5's bulk-assign screen needs tomorrow) owns the state.
 *
 * `getRowLabel`/`selectAllLabel` exist because a checkbox's accessible name can't be derived
 * generically from an arbitrary view-row `T` — same reasoning as `empty` above needing to stay a
 * caller-supplied `ReactNode` rather than a component-guessed default. `getRowLabel` should
 * describe the row's identity (e.g. a material's name), never a bare "select row" — Carbon's own
 * `TableSelectRow` requires a real `aria-label`, and a table of identical unlabeled checkboxes is
 * useless to a screen reader.
 */
export interface DataTableSelectionProps<T extends DataTableRow> {
  selectedIds: Array<string | number>;
  onChange: (ids: Array<string | number>) => void;
  /** Accessible name for one row's checkbox. Called per row — should identify the row (e.g.
   * `(row) => `Select ${row.name}``), not a generic "select row" for every row alike. */
  getRowLabel: (row: T) => string;
  /** Accessible name for the header's select-all checkbox. */
  selectAllLabel: string;
  /**
   * Optional per-row disable (Carbon's own `TableSelectRow` `disabled` prop) — e.g. an item
   * already awarded/delivered can't be re-selected for a new order. Disabled rows are excluded
   * from "select all"'s own all-selected/indeterminate accounting and from what it selects,
   * mirroring the PR-items list's pre-Carbon `toggleSelectAllItems` semantics. Omitted by every
   * consumer that doesn't need it.
   */
  isRowDisabled?: (row: T) => boolean;
  /**
   * When true, a disabled row (per `isRowDisabled`) renders NO checkbox at all — an empty cell in
   * the select column — instead of a visible-but-disabled one. Use this where a disabled checkbox
   * reads as "broken" rather than "not yet selectable" (e.g. the PR-items list, where a
   * not-IN_APPROVAL item simply isn't a quote candidate). Omitted by consumers that prefer the
   * disabled-checkbox affordance.
   */
  hideDisabledSelection?: boolean;
}

export interface DataTableProps<T extends DataTableRow> {
  headers: DataTableColumn<T>[];
  rows: T[];
  isLoading: boolean;
  search?: DataTableSearchProps;
  /**
   * Extra controls rendered in the table toolbar alongside the search — e.g. a status filter
   * `Dropdown`. Makes this a "filtering data table": the filter lives in the toolbar rather than
   * in a separate bar above the table. The toolbar renders whenever `search` OR `toolbarContent`
   * is set, so a filter-only table (no search) still gets one.
   */
  toolbarContent?: ReactNode;
  pagination?: DataTablePaginationProps;
  /** Opt-in sortable headers — see `DataTableSortProps`. Omitted leaves every header plain. */
  sort?: DataTableSortProps;
  /** Opt-in row selection — see `DataTableSelectionProps`. Omitted by every consumer that
   * doesn't need it (Users, Sites today), which is what keeps them byte-unaffected by this. */
  selection?: DataTableSelectionProps<T>;
  /**
   * Opt-in whole-row click (Task 4, PR list) — the PR-requests list's pre-Carbon table made the
   * entire `<tr>` a click target to its detail page, not just one cell's own link (contrast
   * Materials/Suppliers, which only ever link the name cell). Omitted by every other consumer,
   * so this changes nothing for them. When both `selection` and `onRowClick` are set, the
   * selection checkbox's own `TableSelectRow` handles its click before it can bubble to the row.
   */
  onRowClick?: (row: T) => void;
  /**
   * Opt-in coloured status line at the leading edge of a row. Returns a CSS colour for the row's
   * accent bar (an inset left border on the row's first cell), or `undefined` for no bar. Used by
   * the Materials list to show active/inactive as a green/grey line just left of the material code,
   * replacing a separate status column. Omitted by every other consumer, so nothing else changes.
   */
  rowAccent?: (row: T) => string | undefined;
  /**
   * Opt-in expandable rows. When provided, an expand chevron column is added at the leading edge
   * and clicking it toggles a full-width `TableExpandedRow` rendering this node beneath the row
   * (Carbon `TableExpandRow`/`TableExpandedRow`). Expansion state is owned internally (a pure UI
   * toggle). Omitted by every consumer that doesn't need it, so nothing else changes — the chevron
   * column and expand markup only exist when this is passed. The chevron's own click never triggers
   * `onRowClick` (it stops propagation), so a row can both expand and navigate.
   */
  getExpandedContent?: (row: T) => ReactNode;
  /**
   * What to render instead of the table body when `rows` is empty.
   *
   * Required, not defaulted: there is no single message that is right for
   * both "this company has no users yet" and "your search matched
   * nothing" — those need different copy and different actions, and only
   * the caller (which owns `search`) knows which one applies. Forcing
   * every consumer to pass this explicitly, rather than falling back to a
   * generic default when it's omitted, is what keeps that decision from
   * quietly collapsing back into one message for both cases. `DataTable`
   * itself never inspects `search` to pick between messages — it always
   * renders exactly the node it's handed; the two-message split is a
   * property of this type (required, un-defaulted `ReactNode`), not of
   * any runtime branching here.
   */
  empty: ReactNode;
}

/**
 * A resource list: sortable-free, search + pagination optional, loading
 * state via `DataTableSkeleton`. Built from Carbon's `DataTable` family of
 * parts directly (`Table`/`TableHead`/`TableRow`/...) rather than through
 * the stateful `<DataTable>` wrapper, because that wrapper's `rows` shape
 * (`{id, cells: [...]}`, normalized internally) doesn't fit a plain
 * `T[]` of caller-owned resource objects — composing the parts keeps this
 * generic over any row type instead of Carbon's own row shape.
 *
 * Cell values are read as `row[header.key]`. This is a deliberate,
 * lightweight convention rather than a `renderCell` callback: every
 * consumer here already has flat resource objects (a user, a site, a
 * tag), and keeping the contract to `headers`/`rows` (per the brief) means
 * Sites/Tags/Materials can adopt this without inventing per-column render
 * functions. A column whose value needs to be a node (e.g. a row's action
 * menu) can still do that — just include the rendered node as that key's
 * value on the row object.
 *
 * Owned by the primitives layer — Sites/Tags/Materials consume this
 * verbatim. If a flow needs different table behaviour, it changes here,
 * not in a fork.
 */
function DataTable<T extends DataTableRow>({
  headers,
  rows,
  isLoading,
  search,
  toolbarContent,
  pagination,
  sort,
  empty,
  selection,
  onRowClick,
  rowAccent,
  getExpandedContent,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(
    () => new Set()
  );
  const toggleExpand = (id: string | number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const extraLeadingCols = (selection ? 1 : 0) + (getExpandedContent ? 1 : 0);

  const selectableRows = selection
    ? rows.filter((row) => !selection.isRowDisabled?.(row))
    : rows;
  const selectedCount = selection?.selectedIds.length ?? 0;
  const allSelected = Boolean(
    selection &&
      selectableRows.length > 0 &&
      selectedCount === selectableRows.length
  );
  const someSelected = Boolean(selection && selectedCount > 0 && !allSelected);

  const handleSelectAll = () => {
    if (!selection) return;
    selection.onChange(allSelected ? [] : selectableRows.map((row) => row.id));
  };

  const handleSelectRow = (id: string | number) => {
    if (!selection) return;
    const isSelected = selection.selectedIds.includes(id);
    selection.onChange(
      isSelected
        ? selection.selectedIds.filter((selectedId) => selectedId !== id)
        : [...selection.selectedIds, id]
    );
  };

  if (isLoading) {
    return (
      <DataTableSkeleton
        // Carbon's own default is a hardcoded 5 columns regardless of what
        // is actually being rendered — passing `headers` alone does NOT
        // change that (DataTableSkeleton only uses `headers` to label the
        // columns it already built from `columnCount`). Without this, a
        // 3-column table would skeleton-load as 5 columns, then jump to 3
        // the moment real data arrives.
        columnCount={headers.length}
        headers={headers}
        showHeader={false}
        showToolbar={Boolean(search || toolbarContent)}
      />
    );
  }

  return (
    <TableContainer>
      {search || toolbarContent ? (
        <TableToolbar>
          <TableToolbarContent>
            {search ? (
              <TableToolbarSearch
                labelText={search.label}
                placeholder={search.label}
                value={search.value}
                onChange={(_event, value) => search.onChange(value ?? "")}
                onClear={() => search.onChange("")}
                persistent
              />
            ) : null}
            {toolbarContent}
          </TableToolbarContent>
        </TableToolbar>
      ) : null}
      <Table useZebraStyles={false}>
        <TableHead>
          <TableRow>
            {getExpandedContent ? (
              <TableExpandHeader aria-label={t("cta.expandRow")} />
            ) : null}
            {selection ? (
              <TableSelectAll
                id="data-table-select-all"
                name="data-table-select-all"
                aria-label={selection.selectAllLabel}
                checked={allSelected}
                indeterminate={someSelected}
                onSelect={handleSelectAll}
              />
            ) : null}
            {headers.map((header) => {
              if (sort && header.sortable) {
                const isActive = sort.columnKey === header.key;
                return (
                  <TableHeader
                    key={header.key}
                    isSortable
                    isSortHeader={isActive}
                    sortDirection={
                      isActive
                        ? sort.direction === "asc"
                          ? "ASC"
                          : "DESC"
                        : "NONE"
                    }
                    onClick={() => sort.onSort(header.key)}
                  >
                    {header.header}
                  </TableHeader>
                );
              }
              return <TableHeader key={header.key}>{header.header}</TableHeader>;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={headers.length + extraLeadingCols}
                className="data-table__empty-cell"
              >
                {empty}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const accent = rowAccent?.(row);
              const className =
                [
                  onRowClick ? "data-table__row--clickable" : "",
                  accent ? "data-table__row--accented" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined;
              const style = accent
                ? ({ "--row-accent": accent } as React.CSSProperties)
                : undefined;
              const rowDisabled = selection?.isRowDisabled?.(row) ?? false;
              const selectCell = selection ? (
                selection.hideDisabledSelection && rowDisabled ? (
                  // Disabled + hide requested: keep the column aligned but render no checkbox.
                  <TableCell
                    className="cds--table-column-checkbox"
                    aria-hidden="true"
                  />
                ) : (
                  <TableSelectRow
                    id={`data-table-select-row-${row.id}`}
                    name={`data-table-select-row-${row.id}`}
                    aria-label={selection.getRowLabel(row)}
                    checked={selection.selectedIds.includes(row.id)}
                    disabled={rowDisabled}
                    onSelect={() => handleSelectRow(row.id)}
                  />
                )
              ) : null;
              // Cell click navigates only in the expandable path: `TableExpandRow`
              // has no `onClick` of its own (Carbon omits it), so navigation moves
              // onto the data cells there; the plain `TableRow` path keeps its
              // whole-row `onClick` below and needs no per-cell handler.
              const cellClick =
                getExpandedContent && onRowClick
                  ? () => onRowClick(row)
                  : undefined;
              const cells = headers.map((header) => (
                <TableCell key={header.key} onClick={cellClick}>
                  {
                    // `row[header.key]` is fully type-checked at every concrete
                    // call site (the point of `Extract<keyof T, string>` above);
                    // inside this generic body TS can't narrow it to ReactNode,
                    // though the contract requires every value to be display-ready.
                    row[header.key] as ReactNode
                  }
                </TableCell>
              ));

              if (getExpandedContent) {
                const isExpanded = expandedIds.has(row.id);
                return (
                  <Fragment key={row.id}>
                    <TableExpandRow
                      aria-label={t("cta.expandRow")}
                      isExpanded={isExpanded}
                      onExpand={(
                        event: React.MouseEvent<HTMLButtonElement>
                      ) => {
                        event.stopPropagation();
                        toggleExpand(row.id);
                      }}
                      className={className}
                      style={style}
                    >
                      {selectCell}
                      {cells}
                    </TableExpandRow>
                    {isExpanded ? (
                      <TableExpandedRow
                        colSpan={headers.length + extraLeadingCols}
                      >
                        {getExpandedContent(row)}
                      </TableExpandedRow>
                    ) : null}
                  </Fragment>
                );
              }

              return (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={className}
                  style={style}
                >
                  {selectCell}
                  {cells}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {pagination ? (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          pageSizes={pagination.pageSizes ?? [pagination.pageSize]}
          totalItems={pagination.totalItems}
          onChange={({ page, pageSize }) =>
            pagination.onPageChange(page, pageSize)
          }
          backwardText={t("pagination.previous")}
          forwardText={t("pagination.next")}
          itemsPerPageText={t("pagination.itemsPerPage")}
          itemRangeText={(min, max, total) =>
            `${min}–${max} ${t("pagination.of")} ${total} ${t(
              "pagination.results"
            )}`
          }
          itemText={(min, max) => `${min}–${max} ${t("pagination.results")}`}
          // Visible whenever there's more than one page — the normal case,
          // not an edge case — so these need the same t() treatment as
          // everything else here. Reuses the same pagination.page/of keys
          // PurchaseRequestsV2's own "Page X of Y" text already uses,
          // rather than reproducing Carbon's English "of N pages"
          // singular/plural default, which sidesteps needing a translated
          // "page"/"pages" plural form per locale.
          //
          // `pageText` (Carbon's default: `page ${page}`) is deliberately
          // left untranslated: it only renders when `pagesUnknown` is set,
          // which this component never does, so it can't surface.
          pageRangeText={(current, total) =>
            `${t("pagination.page")} ${current} ${t("pagination.of")} ${total}`
          }
          pageSelectLabelText={(total) =>
            `${t("pagination.page")} ${t("pagination.of")} ${total}`
          }
        />
      ) : null}
    </TableContainer>
  );
}

export default DataTable;
